import React, { useRef } from "react";
import type { Appointment } from "./types";
import { fmtHm, snapMinutes, SLOT_MIN } from "./time";

export const APPT_MIME = "application/x-appointment-id";
/** Pixel height of one 15-minute slot — keep in sync with Calendar.css. */
export const SLOT_H = 16;
export const PX_PER_MIN = SLOT_H / SLOT_MIN;
const MIN_DURATION_MIN = 15;
const MAX_DURATION_MIN = 480;

interface Props {
  appointment: Appointment;
  patientName: string;
  isRecurring: boolean;
  top: number;
  height: number;
  lane: number;
  laneCount: number;
  onSelect: (a: Appointment) => void;
  onResize: (id: string, durationMin: number) => void;
}

export default function AppointmentBlock({
  appointment: a, patientName, isRecurring, top, height, lane, laneCount,
  onSelect, onResize,
}: Props) {
  const resizing = useRef<{ startY: number; startHeight: number } | null>(null);
  const start = new Date(a.start_at);
  const end = new Date(a.end_at);
  const draggable = a.status === "scheduled";
  const widthPct = 100 / laneCount;

  const startResize = (e: React.PointerEvent<HTMLDivElement>) => {
    // Resize uses pointer events, NOT HTML5 DnD (DnD cannot express resize).
    e.stopPropagation();
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    resizing.current = { startY: e.clientY, startHeight: height };
  };

  const moveResize = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizing.current) return;
    // Purely visual feedback is skipped (economy); commit happens on release.
    e.preventDefault();
  };

  const endResize = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizing.current) return;
    const { startY, startHeight } = resizing.current;
    resizing.current = null;
    const newHeightPx = startHeight + (e.clientY - startY);
    const snapped = snapMinutes(newHeightPx / PX_PER_MIN);
    const bounded = Math.min(MAX_DURATION_MIN, Math.max(MIN_DURATION_MIN, snapped));
    const current = Math.round((end.getTime() - start.getTime()) / 60_000);
    if (bounded !== current) onResize(a.appointment_id, bounded);
  };

  return (
    <div
      data-testid={`appt-${a.appointment_id}`}
      className={`cal-block cal-block--${a.status}${isRecurring ? " cal-block--series" : ""}`}
      draggable={draggable || undefined}
      onDragStart={
        draggable
          ? (e) => {
              e.dataTransfer.setData(APPT_MIME, a.appointment_id);
              e.dataTransfer.effectAllowed = "move";
            }
          : undefined
      }
      onClick={(e) => {
        e.stopPropagation();
        onSelect(a);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(a);
        }
      }}
      style={{ top, height, width: `calc(${widthPct}% - 2px)`, left: `${lane * widthPct}%` }}
    >
      <span className="cal-block-name">{patientName}</span>
      <span className="cal-block-time">
        {fmtHm(start)}–{fmtHm(end)}
      </span>
      <span className="cal-block-badges">
        {/* gcal_sync rendering extended in Chunk 4 Task 19 (connect chip, failed count) */}
        {isRecurring && "↻"}
        {a.reminder_sent_at && "✓"}
        {(a.reminder_failed || a.gcal_sync === "failed") && "⚠"}
        {a.session_id && "●"}
      </span>
      {a.status === "scheduled" && (
        <div
          className="cal-block-resize"
          data-testid={`resize-${a.appointment_id}`}
          onPointerDown={startResize}
          onPointerMove={moveResize}
          onPointerUp={endResize}
        />
      )}
    </div>
  );
}
